import { useMemo } from 'react'
import { useAuth } from '../../../../../../../auth'
import { TableState } from '../../../../../../../hooks/use-table-state'
import { UserPermissions } from '../../../../../../../types/permissions'
import { RouteMode } from '../../../../../../../types/route'
import { FormElementConfig, ProposedTotals } from '../../../../../../../forms/types'
import { toDollarCurrencyFormat } from '../../../../../../../forms/utils'
import { ApplicationDetailsFields } from './config'
import { PLANTING_TABLE_COLUMNS } from '../../../../../../../forms/constants'
import { sqmToHa } from '../../../../../../../utils/units'

interface PlantingConfigProps extends Pick<TableState, 'refreshTable' | 'deleteAction'> {
  mode?: RouteMode
  isMigrated?: boolean
  applicationId?: string
  planId?: string
  data?: { proposedTotals: ProposedTotals }
}

export const usePlantingConfig = ({
  mode,
  refreshTable,
  isMigrated,
  planId,
  applicationId,
  data,
  deleteAction,
}: PlantingConfigProps): FormElementConfig<ApplicationDetailsFields>[] => {
  const { user } = useAuth()
  return useMemo(() => {
    if (isMigrated)
      return [
        {
          type: 'field',
          element: {
            type: 'repeating-section',
            heading: 'Planting',
            sectionName: 'Planting action',
            repeating: true,
            fieldId: 'plantingActions',
            fields: [
              {
                type: 'lookup',
                fieldId: 'type',
                lookupId: 'action_subtype',
                fieldFilters: { feature_category: 'planting' },
              },
              {
                type: 'number',
                fieldId: 'area',
              },
              {
                type: 'number',
                fieldId: 'proposedCost',
              },
              {
                type: 'number',
                fieldId: 'numberOfPlants',
              },
              {
                type: 'number',
                fieldId: 'numberOfPlantsZoneA',
              },
              {
                type: 'number',
                fieldId: 'numberOfPlantsZoneB',
              },
              {
                type: 'number',
                fieldId: 'numberOfPlantsZoneC',
              },
            ],
          },
        },
      ]
    return [
      {
        type: 'atom',
        element: { type: 'heading', level: 2, content: 'Planting' },
      },
      {
        type: 'field',
        element: {
          type: 'table',
          currentUserPermissions: user?.permissions,
          columnHeaders: [
            ...PLANTING_TABLE_COLUMNS,
            {
              elementKey: 'actions',
              columnHeading: '',
              viewColumn: 'actions',
              type: 'actions-no-data',
              actions:
                mode == RouteMode.EDIT
                  ? [
                      {
                        label: 'Delete',
                        type: 'deleteAction',
                        args: [
                          {
                            elementKey: 'id',
                          },
                        ],
                        actionPermissions: [UserPermissions.EDIT_PLANS],
                      },
                    ]
                  : [],
            },
          ],
          refreshTable: refreshTable,
          tableActionHandlers: {
            deleteAction,
          },
          defaultSortBy: {
            orderColumn: 'locationName',
            orderDirection: 'desc',
          },
          requestUrl: `/api/plan/${planId}/project/${applicationId}/feature/planting`,
          rowClickAction: {
            label: 'View',
            type: 'transitionAction',
            args: [{ constant: `plans/${mode}/${planId}/plan/project/${applicationId}/action` }, { elementKey: 'id' }],
          },
          noResultsMessage: 'No planting actions added',
        },
      },
      {
        type: 'field',
        element: {
          type: 'read-only',
          fieldId: 'proposedTotals.plantingArea',
          conversion: sqmToHa,
        },
      },
      {
        type: 'field',
        element: {
          type: 'read-only',
          fieldId: 'proposedTotals.plantingPlantCount',
        },
      },
      {
        type: 'field',
        element: {
          type: 'read-only',
          fieldId: 'proposedTotals.plantingPlantCost',
          helpText:
            data?.proposedTotals?.plantingPlantCost == '0' && data?.proposedTotals?.plantingCost
              ? 'The plant cost is $0 because the value was set before separate plant prices were calculated.'
              : '',
          conversion: toDollarCurrencyFormat,
        },
      },
      {
        type: 'field',
        element: {
          type: 'read-only',
          fieldId: 'proposedTotals.plantingLabourCost',
          helpText:
            data?.proposedTotals?.plantingLabourCost == '0' && data?.proposedTotals?.plantingCost
              ? 'The labour cost is $0 because the value was set before separate labour prices were calculated.'
              : '',
          conversion: toDollarCurrencyFormat,
        },
      },
      {
        type: 'field',
        element: {
          type: 'read-only',
          fieldId: 'proposedTotals.plantingCost',
          conversion: toDollarCurrencyFormat,
        },
      },
    ]
  }, [mode, refreshTable, isMigrated, planId, applicationId, deleteAction])
}
